/**
 * 📅 남은 납품 일정 (v2.277.0 · 2026-10-19 · 사용자 「웍스엔 9/30 성북 일정이 2개 · 원래 1개 · 하나를 지워줘」) — 시스템 관리 › 연동 도구
 *
 * 같은 날·같은 엔드유저 영업건은 웍스 일정 1건으로 묶인다(묶인 영업건이 모두 같은 일정 번호를 가리킴).
 * 「남은 일정」 = 포털이 만든 납품 일정(제목 [납품…] + 메모에 영업번호)인데 **어느 영업건도 가리키지 않는 것** —
 *   다시 만들기가 겹치거나(10-15 계산서 두 장 · v2.277.0 부터 줄 세움) 지우기에 실패해서 남는다.
 * [찾기] → 목록(날짜·제목·만든 때·메모의 영업번호) → 고른 것 [웍스에서 지우기]. 지우기 직전 서버가 다시 훑어 그때도 남은 것만 지운다.
 * ⚠영업건·포털 자료는 건드리지 않는다 — 웍스 일정만.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import * as adminApi from '../lib/admin-api';
import type { DeliveryOrphan } from '../lib/admin-api';

function ymd(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function DeliveryOrphanCard() {
  const today = new Date();
  const [from, setFrom] = useState(() => ymd(new Date(today.getFullYear(), today.getMonth() - 3, 1)));
  const [to, setTo] = useState(() => ymd(new Date(today.getFullYear(), today.getMonth() + 4, 0)));
  const [found, setFound] = useState<{ scanned: number; orphans: DeliveryOrphan[]; from: string; to: string } | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [done, setDone] = useState<string | null>(null);

  const scanM = useMutation({
    mutationFn: () => adminApi.listDeliveryOrphans(from, to),
    onSuccess: (r) => { setFound(r); setPicked(new Set(r.orphans.map((o) => o.eventId))); setDone(null); },
    onError: (e: Error) => alert(`찾기 실패: ${e.message}`),
  });
  const delM = useMutation({
    mutationFn: () => adminApi.deleteDeliveryOrphans(found!.from, found!.to, [...picked]),
    onSuccess: (r) => {
      const failed = r.results.filter((x) => !x.ok);
      setDone(`웍스에서 ${r.deleted}건 지웠습니다${failed.length ? ` · 못 지움 ${failed.length}건(${failed.map((f) => f.reason).join(' / ')})` : ''}`);
      scanM.mutate();
    },
    onError: (e: Error) => alert(`지우기 실패: ${e.message}`),
  });

  const toggle = (id: string) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <div className="mt-6 rounded border border-slate-200 bg-white p-4 shadow-sm">
      <h2 className="ui-section-title mb-1">📅 남은 납품 일정 (네이버웍스)</h2>
      <p className="mb-3 text-xs text-slate-500">
        같은 날·같은 엔드유저 영업건은 웍스 일정 <b>1건</b>으로 묶입니다. 포털이 만든 납품 일정인데 <b>어느 영업건도 가리키지 않는 일정</b>(겹쳐 만들어졌거나 지우다 남은 것)을 찾아 지웁니다.
        영업건·포털 자료는 건드리지 않고 웍스 일정만 지웁니다.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <label className="ui-label">납품예정일</label>
        <input type="date" className="ui-input" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="시작 날짜" />
        <span className="text-slate-400">~</span>
        <input type="date" className="ui-input" value={to} onChange={(e) => setTo(e.target.value)} aria-label="끝 날짜" />
        <button type="button" className="btn btn-secondary" onClick={() => scanM.mutate()} disabled={scanM.isPending}>
          {scanM.isPending ? '찾는 중…' : '찾기'}
        </button>
      </div>
      {done && <p className="mt-2 rounded border border-emerald-200 bg-emerald-50 px-2 py-1 text-sm text-emerald-800">{done}</p>}
      {found && (
        <div className="mt-3">
          <p className="text-sm text-slate-600">
            {found.from} ~ {found.to} · 웍스 일정 {found.scanned}건 중 남은 납품 일정 <b>{found.orphans.length}</b>건
          </p>
          {found.orphans.length > 0 && (
            <>
              <ul className="mt-2 space-y-1.5">
                {found.orphans.map((o) => (
                  <li key={o.eventId} className="rounded border border-amber-200 bg-amber-50/50 px-2 py-1.5 text-sm">
                    <label className="flex items-start gap-2">
                      <input type="checkbox" className="mt-1" checked={picked.has(o.eventId)} onChange={() => toggle(o.eventId)} />
                      <span className="min-w-0 flex-1">
                        <span className="font-medium text-slate-800">{o.date}</span> <span className="text-slate-800">{o.summary}</span>
                        <span className="ml-1 text-xs text-slate-500">
                          {o.createdAt ? `· 만든 때 ${o.createdAt}` : ''}{o.hasLiveTwin ? ' · 같은 날 같은 제목의 포털 일정이 따로 있음(겹친 사본)' : ''}
                        </span>
                        <span className="block text-xs text-slate-500">메모의 영업번호: {o.salesIds.join(', ') || '-'}</span>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
              <div className="mt-2 flex items-center gap-2">
                <button
                  type="button"
                  className="btn btn-danger"
                  disabled={picked.size === 0 || delM.isPending}
                  onClick={() => {
                    if (!window.confirm(`고른 ${picked.size}건을 네이버웍스 캘린더에서 지웁니다.\n영업건이 가리키는 일정은 지우지 않습니다(지우기 직전에 다시 확인).\n\n계속할까요?`)) return;
                    delM.mutate();
                  }}
                >
                  {delM.isPending ? '지우는 중…' : `고른 ${picked.size}건 웍스에서 지우기`}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
